import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PanelHead, Badge, Empty, Field, Area, Btn, useFocusRecord, focusCls } from "./common";
import { Plus, Trash2, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ProjectWork } from "./ProjectWork";

function useCollection(path) {
  const [items, setItems] = useState([]);
  const load = async () => { try { const r = await api.get(path); setItems(r.data); } catch (e) {} };
  useEffect(() => { load(); }, [path]);
  return { items, load };
}

const statusColor = { open: "cyan", "in_progress": "amber", done: "green", planning: "blue",
  active: "green", completed: "gray", cancelled: "red", on_hold: "amber" };

// ---------------- TASKS ----------------
export const TasksPanel = ({ onChanged, focusId }) => {
  const { t, tx } = useI18n();
  const { items, load } = useCollection("/tasks");
  useFocusRecord(items, focusId);
  const [form, setForm] = useState(null);
  const blank = { title: "", priority: "normal", status: "open", due_date: "", notes: "" };

  const save = async () => {
    if (!form.title.trim()) return toast.error(tx("Başlık gerekli"));
    if (form.task_id) await api.put(`/tasks/${form.task_id}`, form);
    else await api.post("/tasks", form);
    setForm(null); load(); onChanged?.();
  };
  const toggle = async (x) => { await api.put(`/tasks/${x.task_id}`, { status: x.status === "done" ? "open" : "done" }); load(); onChanged?.(); };
  const del = async (id) => { await api.delete(`/tasks/${id}`); load(); onChanged?.(); };

  return (
    <div data-testid="panel-tasks">
      <PanelHead title={t("nav_tasks")} sub={tx("Görev yönetimi")}
        right={<Btn data-testid="add-task-btn" onClick={() => setForm(blank)}><Plus size={15} className="inline mr-1" />{t("add")}</Btn>} />
      {form && (
        <div className="panel p-4 mb-5">
          <Field label={tx("Başlık")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" />
          <div className="grid grid-cols-3 gap-3">
            <Sel label={tx("Öncelik")} value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} opts={["low", "normal", "high"]} />
            <Sel label={tx("Durum")} value={form.status} onChange={(v) => setForm({ ...form, status: v })} opts={["open", "in_progress", "done"]} />
            <Field label={tx("Teslim")} type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <Area label={tx("Not")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex gap-2"><Btn onClick={save} data-testid="save-task-btn">{t("save")}</Btn><Btn variant="ghost" onClick={() => setForm(null)}>{t("cancel")}</Btn></div>
        </div>
      )}
      <div className="space-y-2 stagger">
        {items.length === 0 && <Empty text={t("noData")} />}
        {items.map((x) => (
          <div key={x.task_id} data-rid={x.task_id} className={`panel p-3.5 flex items-center justify-between${focusCls(x.task_id, focusId)}`} data-testid="task-row">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => toggle(x)} data-testid="toggle-task-btn"
                className={`h-5 w-5 rounded border flex items-center justify-center ${x.status === "done" ? "bg-emerald-500/40 border-emerald-400" : "border-cyan-400/40"}`}>
                {x.status === "done" && <Check size={13} />}
              </button>
              <div className="min-w-0">
                <div className={`text-sm ${x.status === "done" ? "line-through text-white/40" : "text-white"}`}>{x.title}</div>
                {x.due_date && <div className="label-eyebrow">{x.due_date}</div>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge color={x.priority === "high" ? "red" : "cyan"}>{x.priority}</Badge>
              <button onClick={() => setForm(x)} className="text-white/50 hover:text-cyan-300"><Pencil size={15} /></button>
              <button onClick={() => del(x.task_id)} className="text-white/50 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------- CLIENTS ----------------
export const ClientsPanel = ({ onChanged, focusId }) => {
  const { t, tx } = useI18n();
  const { items, load } = useCollection("/clients");
  useFocusRecord(items, focusId);
  const [form, setForm] = useState(null);
  const blank = { name: "", email: "", phone: "", address: "", notes: "" };
  const save = async () => {
    if (!form.name.trim()) return toast.error(tx("İsim gerekli"));
    if (form.client_id) await api.put(`/clients/${form.client_id}`, form); else await api.post("/clients", form);
    setForm(null); load(); onChanged?.();
  };
  const del = async (id) => { await api.delete(`/clients/${id}`); load(); onChanged?.(); };
  return (
    <div data-testid="panel-clients">
      <PanelHead title={t("nav_clients")} sub={tx("Müşteri rehberi")}
        right={<Btn data-testid="add-client-btn" onClick={() => setForm(blank)}><Plus size={15} className="inline mr-1" />{t("add")}</Btn>} />
      {form && (
        <div className="panel p-4 mb-5">
          <div className="grid md:grid-cols-2 gap-3 stagger">
            <Field label={tx("İsim")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" />
            <Field label={tx("E-posta")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Field label={tx("Telefon")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Field label={tx("Adres")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <Area label={tx("Not")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex gap-2"><Btn onClick={save} data-testid="save-client-btn">{t("save")}</Btn><Btn variant="ghost" onClick={() => setForm(null)}>{t("cancel")}</Btn></div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3 stagger">
        {items.length === 0 && <Empty text={t("noData")} />}
        {items.map((x) => (
          <div key={x.client_id} data-rid={x.client_id} className={`panel p-4${focusCls(x.client_id, focusId)}`} data-testid="client-row">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-white font-medium">{x.name}</div>
                <div className="text-xs text-[color:var(--txt-2)] mt-1">{x.email} {x.phone && `· ${x.phone}`}</div>
                {x.address && <div className="text-xs text-[color:var(--txt-3)] mt-0.5">{x.address}</div>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setForm(x)} className="text-white/50 hover:text-cyan-300"><Pencil size={15} /></button>
                <button onClick={() => del(x.client_id)} className="text-white/50 hover:text-red-400"><Trash2 size={15} /></button>
              </div>
            </div>
            {x.notes && <p className="text-xs text-white/60 mt-2">{x.notes}</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------- PROJECTS ----------------
export const ProjectsPanel = ({ onChanged, focusId }) => {
  const { t, tx } = useI18n();
  const { items, load } = useCollection("/projects");
  useFocusRecord(items, focusId);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(null);
  const [openWork, setOpenWork] = useState(null);
  const blank = { name: "", client_id: "", address: "", scope: "", status: "planning", progress: 0, notes: "", due_date: "" };
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, [items]);
  const save = async () => {
    if (!form.name.trim()) return toast.error(tx("Proje adı gerekli"));
    if (form.project_id) await api.put(`/projects/${form.project_id}`, form); else await api.post("/projects", form);
    setForm(null); load(); onChanged?.();
  };
  const del = async (id) => { await api.delete(`/projects/${id}`); load(); onChanged?.(); };
  return (
    <div data-testid="panel-projects">
      <PanelHead title={t("nav_projects")} sub={tx("Aktif şantiyeler")}
        right={<Btn data-testid="add-project-btn" onClick={() => setForm(blank)}><Plus size={15} className="inline mr-1" />{t("add")}</Btn>} />
      {form && (
        <div className="panel p-4 mb-5">
          <div className="grid md:grid-cols-2 gap-3 stagger">
            <Field label={tx("Proje adı")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" />
            <Field label={tx("Adres")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <Sel label={tx("Müşteri")} value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })}
              opts={[["", "—"], ...clients.map((c) => [c.client_id, c.name])]} raw />
            <Sel label={tx("Durum")} value={form.status} onChange={(v) => setForm({ ...form, status: v })}
              opts={["planning", "active", "on_hold", "completed", "cancelled"]} />
            <Field label={tx("İlerleme %")} type="number" value={form.progress} onChange={(e) => setForm({ ...form, progress: +e.target.value })} />
            <Field label={tx("Bitiş")} type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <Area label={tx("Kapsam")} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} />
          <div className="flex gap-2"><Btn onClick={save} data-testid="save-project-btn">{t("save")}</Btn><Btn variant="ghost" onClick={() => setForm(null)}>{t("cancel")}</Btn></div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3 stagger">
        {items.length === 0 && <Empty text={t("noData")} />}
        {items.map((x) => (
          <div key={x.project_id} data-rid={x.project_id} className={`panel p-4${focusCls(x.project_id, focusId)}`} data-testid="project-row">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="text-white font-medium">{x.name}</div>
                <div className="text-xs text-[color:var(--txt-2)] mt-0.5">{x.address}</div>
              </div>
              <div className="flex gap-2">
                <Badge color={statusColor[x.status] || "cyan"}>{x.status}</Badge>
                <button onClick={() => setForm(x)} className="text-white/50 hover:text-cyan-300"><Pencil size={15} /></button>
                <button onClick={() => del(x.project_id)} className="text-white/50 hover:text-red-400"><Trash2 size={15} /></button>
              </div>
            </div>
            {x.scope && <p className="text-xs text-white/60 mt-2 line-clamp-2">{x.scope}</p>}
            <div className="mt-3 h-1.5 bg-black/40 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${x.progress || 0}%` }} />
            </div>
            <button onClick={() => setOpenWork(openWork === x.project_id ? null : x.project_id)} data-testid={`project-work-${x.project_id}`}
              aria-expanded={openWork === x.project_id} className="mt-2 text-[0.7rem] text-cyan-300/80 hover:text-cyan-200">
              {openWork === x.project_id ? "▾" : "▸"} {tx("Notlar · kapsam · proje hafızası")}
            </button>
            {openWork === x.project_id && <ProjectWork project={x} onChanged={() => { load(); onChanged?.(); }} />}
          </div>
        ))}
      </div>
    </div>
  );
};

const Sel = ({ label, value, onChange, opts, raw }) => (
  <label className="block mb-3">
    <span className="label-eyebrow block mb-1">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400/60">
      {opts.map((o) => raw ? <option key={o[0]} value={o[0]} className="bg-[#0C1222]">{o[1]}</option>
        : <option key={o} value={o} className="bg-[#0C1222]">{o}</option>)}
    </select>
  </label>
);

export default TasksPanel;
